import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../lib/api.js';
import { PageHeader } from '../components/PageHeader.js';

interface ApiKey {
  api_key_id: string;
  name: string;
  prefix: string;
  last_used_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

export function ApiKeys() {
  const api = useApi();
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['api-keys'],
    queryFn: () => api.get<{ items: ApiKey[] }>('/api-keys'),
  });
  const [name, setName] = useState('');
  const [newKey, setNewKey] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => api.post<{ api_key_id: string; key: string; prefix: string }>('/api-keys', { name }),
    onSuccess: (resp) => {
      setNewKey(resp.key);
      setName('');
      qc.invalidateQueries({ queryKey: ['api-keys'] });
    },
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.post(`/api-keys/${id}/revoke`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['api-keys'] }),
  });

  return (
    <div className="p-6">
      <PageHeader title="API keys" subtitle="Use these to import products and integrate from CLI/scripts." />

      <div className="rounded-lg border border-slate-200 bg-white p-4 mb-6">
        <h3 className="font-medium mb-2">Create a new key</h3>
        <div className="flex gap-2">
          <input
            placeholder="Key name (e.g. Sooqy importer)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="flex-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm"
          />
          <button
            disabled={!name || create.isPending}
            onClick={() => create.mutate()}
            className="rounded-md bg-brand-600 text-white px-3 py-1.5 text-sm disabled:opacity-50"
          >
            Create
          </button>
        </div>
      </div>

      {newKey && (
        <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-4 mb-6">
          <div className="font-semibold text-amber-900">Copy this key — it's shown only once</div>
          <pre className="mt-2 bg-white border border-amber-200 rounded p-2 text-xs break-all select-all">{newKey}</pre>
          <button onClick={() => { navigator.clipboard.writeText(newKey); }} className="mt-2 text-xs underline">Copy</button>
        </div>
      )}

      <ul className="space-y-2">
        {(data?.items ?? []).map((k) => (
          <li key={k.api_key_id} className="rounded-lg border border-slate-200 bg-white p-3 flex items-center justify-between">
            <div>
              <div className="font-medium">{k.name}</div>
              <div className="text-xs text-slate-500 font-mono">{k.prefix}…</div>
              <div className="text-xs text-slate-400">created {new Date(k.created_at).toLocaleDateString()}{k.last_used_at ? ` · last used ${new Date(k.last_used_at).toLocaleString()}` : ''}{k.revoked_at ? ` · revoked` : ''}</div>
            </div>
            {!k.revoked_at && (
              <button onClick={() => revoke.mutate(k.api_key_id)} className="text-sm text-red-600 hover:underline">Revoke</button>
            )}
          </li>
        ))}
        {(data?.items?.length ?? 0) === 0 && <li className="text-sm text-slate-500">No keys yet.</li>}
      </ul>
    </div>
  );
}
